import { API_URL, API_VERSION } from '@constants';
import { useData } from '@hooks/useData';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { getSessionId } from '@utils/session';

const ENDPOINT_URL = '/str-stores/schedule/{uuid}';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;


export type GetSchedulePathInput = paths[url]['post']['parameters']['path'];
export type GetScheduleBodyInput = paths[url]['post']['requestBody']['content']['application/json'];
export interface GetScheduleSuccessResponse {
    data: paths[url]['post']['responses']['200']['content']['application/json'];
}

/**
 * Requests endpoint to get store by uuid.
 */
export const useGetSchedule = () => {
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const { setStoreSchedule, setIsLoadingStoreSchedule } = useData();

    const request = async (pathProps: GetSchedulePathInput, bodyProps: GetScheduleBodyInput) => {
        setIsLoadingStoreSchedule(true);

        const sessionId = getSessionId();

        try {
            const commonQueryString = new URLSearchParams(getCommonParameters()).toString();
            const sessionQueryString = `session_id=${encodeURIComponent(sessionId)}`;
            const fullQueryString = `${commonQueryString}&${sessionQueryString}`;

            const response = await fetch(`${API_URL}${ENDPOINT_URL.replace('{uuid}', pathProps.uuid)}?${fullQueryString}`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ ...getCommonParameters(), ...bodyProps }),
            });

            if (!response.ok) {
                const json = await response.json();
                const error = new Error(json.error || '') as ApiError;
                error.statusCode = response.status;
                throw error;
            }

            const data: GetScheduleSuccessResponse = await response.json();

            setStoreSchedule(data);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            handleCommonErrors(error);
        } finally {
            setIsLoadingStoreSchedule(false);
        }
    };

    return { request };
};
